import React, { useState, useEffect } from 'react';
import { movementsApi, productsApi, warehousesApi } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import {
  Plus,
  ArrowLeftRight,
  ArrowDownCircle,
  ArrowUpCircle,
  RefreshCw,
  Loader2,
  Trash2
} from 'lucide-react';

const MovementsPage = () => {
  const { isOperator } = useAuth();
  const [movements, setMovements] = useState([]);
  const [products, setProducts] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [meta, setMeta] = useState({ total: 0 });
  
  const [formData, setFormData] = useState({
    movement_type: 'IN',
    reference: '',
    reason: '',
    notes: '',
    lines: [{ product_id: '', quantity: '', unit_cost: '', warehouse_id: '', warehouse_destination_id: '' }]
  });

  const movementTypes = [
    { value: 'IN', label: 'Entrada', icon: ArrowDownCircle, color: 'emerald' },
    { value: 'OUT', label: 'Salida', icon: ArrowUpCircle, color: 'red' },
    { value: 'ADJUST', label: 'Ajuste', icon: RefreshCw, color: 'amber' },
    { value: 'TRANSFER', label: 'Transferencia', icon: ArrowLeftRight, color: 'blue' }
  ];

  const fetchMovements = async () => {
    try {
      setLoading(true);
      const response = await movementsApi.list({ limit: 100 });
      setMovements(response.data.data);
      setMeta(response.data.meta);
    } catch (error) {
      toast.error('Error al cargar movimientos');
    } finally {
      setLoading(false);
    }
  };

  const fetchCatalogs = async () => {
    try {
      const [productsRes, warehousesRes] = await Promise.all([
        productsApi.list({ is_active: true }),
        warehousesApi.list()
      ]);
      setProducts(productsRes.data.data);
      setWarehouses(warehousesRes.data.data);
    } catch (error) {
      console.error('Error fetching catalogs:', error);
    }
  };

  useEffect(() => {
    fetchMovements();
    fetchCatalogs();
  }, []);

  const openCreateDialog = () => {
    setFormData({
      movement_type: 'IN',
      reference: '',
      reason: '',
      notes: '',
      lines: [{ product_id: '', quantity: '', unit_cost: '', warehouse_id: '', warehouse_destination_id: '' }]
    });
    setDialogOpen(true);
  };

  const addLine = () => {
    setFormData({
      ...formData,
      lines: [...formData.lines, { product_id: '', quantity: '', unit_cost: '', warehouse_id: '', warehouse_destination_id: '' }]
    });
  };

  const removeLine = (index) => {
    if (formData.lines.length === 1) return;
    setFormData({
      ...formData,
      lines: formData.lines.filter((_, i) => i !== index)
    });
  };

  const updateLine = (index, field, value) => {
    const newLines = [...formData.lines];
    newLines[index][field] = value;
    setFormData({ ...formData, lines: newLines });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const payload = {
        movement_type: formData.movement_type,
        reference: formData.reference,
        reason: formData.reason,
        notes: formData.notes,
        lines: formData.lines.map(line => ({
          product_id: line.product_id,
          quantity: parseFloat(line.quantity),
          unit_cost: parseFloat(line.unit_cost) || 0,
          warehouse_id: line.warehouse_id,
          warehouse_destination_id: line.warehouse_destination_id || null
        }))
      };

      await movementsApi.create(payload);
      toast.success('Movimiento registrado');
      setDialogOpen(false);
      fetchMovements();
    } catch (error) {
      toast.error(error.response?.data?.detail?.message || 'Error al registrar movimiento');
    } finally {
      setSaving(false);
    }
  };

  const getMovementBadge = (type) => {
    const config = movementTypes.find(t => t.value === type);
    const colorClasses = {
      emerald: 'bg-emerald-50 text-emerald-700 border-emerald-200',
      red: 'bg-red-50 text-red-700 border-red-200',
      amber: 'bg-amber-50 text-amber-700 border-amber-200',
      blue: 'bg-blue-50 text-blue-700 border-blue-200'
    };
    return { label: config?.label || type, className: colorClasses[config?.color] || 'bg-slate-100' };
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="movements-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 tracking-tight">Movimientos</h1>
          <p className="text-slate-500 mt-1">Kardex y registro de movimientos de inventario</p>
        </div>
        {isOperator() && (
          <Button 
            onClick={openCreateDialog}
            className="bg-blue-900 hover:bg-blue-800"
            data-testid="create-movement-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Nuevo Movimiento
          </Button>
        )}
      </div>

      {/* Movements Table */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center gap-2">
            <ArrowLeftRight className="w-5 h-5" />
            Historial de Movimientos
            <Badge variant="secondary" className="ml-2">{meta.total}</Badge>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-blue-900" />
            </div>
          ) : movements.length === 0 ? (
            <div className="text-center py-12">
              <ArrowLeftRight className="w-12 h-12 mx-auto text-slate-300 mb-4" />
              <p className="text-slate-500">No hay movimientos registrados</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-slate-100">
                    <th className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wider py-3 px-4">Fecha</th>
                    <th className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wider py-3 px-4">Tipo</th>
                    <th className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wider py-3 px-4">Referencia</th>
                    <th className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wider py-3 px-4">Productos</th>
                    <th className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wider py-3 px-4">Motivo</th>
                  </tr>
                </thead>
                <tbody>
                  {movements.map((mov) => {
                    const badge = getMovementBadge(mov.movement_type);
                    return (
                      <tr key={mov.id} className="border-b border-slate-50 hover:bg-slate-50 transition-colors">
                        <td className="py-3 px-4 text-sm text-slate-600 tabular-nums">
                          {new Date(mov.created_at).toLocaleString('es-MX', { 
                            dateStyle: 'short', 
                            timeStyle: 'short' 
                          })}
                        </td>
                        <td className="py-3 px-4">
                          <Badge className={badge.className}>{badge.label}</Badge>
                        </td>
                        <td className="py-3 px-4 text-sm text-slate-900 font-medium">
                          {mov.reference || '-'}
                        </td>
                        <td className="py-3 px-4">
                          <div className="space-y-1">
                            {mov.lines?.slice(0, 2).map((line, idx) => (
                              <div key={idx} className="text-sm">
                                <span className="font-medium text-slate-900">{line.product_name || line.product_sku}</span>
                                <span className="text-slate-500 ml-2">x{line.quantity}</span>
                              </div>
                            ))}
                            {mov.lines?.length > 2 && (
                              <p className="text-xs text-slate-500">+{mov.lines.length - 2} más</p>
                            )}
                          </div>
                        </td>
                        <td className="py-3 px-4 text-sm text-slate-600 max-w-xs truncate">
                          {mov.reason || '-'}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create Movement Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Nuevo Movimiento de Inventario</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Movement Type */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {movementTypes.map((type) => (
                <button
                  key={type.value}
                  type="button"
                  onClick={() => setFormData({ ...formData, movement_type: type.value })}
                  className={`p-4 rounded-md border-2 transition-colors ${
                    formData.movement_type === type.value
                      ? 'border-blue-900 bg-blue-50'
                      : 'border-slate-200 hover:border-slate-300'
                  }`}
                >
                  <type.icon className={`w-6 h-6 mx-auto mb-2 ${
                    formData.movement_type === type.value ? 'text-blue-900' : 'text-slate-400'
                  }`} />
                  <p className={`text-sm font-medium ${
                    formData.movement_type === type.value ? 'text-blue-900' : 'text-slate-600'
                  }`}>{type.label}</p>
                </button>
              ))}
            </div>

            {/* Reference & Reason */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="reference">Referencia</Label>
                <Input
                  id="reference"
                  value={formData.reference}
                  onChange={(e) => setFormData({ ...formData, reference: e.target.value })}
                  placeholder="Ej: Venta #1001"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="reason">Motivo</Label>
                <Input
                  id="reason"
                  value={formData.reason}
                  onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
                  placeholder="Ej: Salida por venta"
                />
              </div>
            </div>

            {/* Lines */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <Label>Líneas del movimiento</Label>
                <Button type="button" variant="outline" size="sm" onClick={addLine}>
                  <Plus className="w-4 h-4 mr-1" /> Agregar línea
                </Button>
              </div>

              {formData.lines.map((line, index) => (
                <div key={index} className="p-4 bg-slate-50 rounded-md space-y-4">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-slate-700">Línea {index + 1}</span>
                    {formData.lines.length > 1 && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => removeLine(index)}
                        className="text-red-600 hover:text-red-700"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    <div className="space-y-2">
                      <Label>Producto</Label>
                      <Select
                        value={line.product_id}
                        onValueChange={(value) => updateLine(index, 'product_id', value)}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Seleccionar" />
                        </SelectTrigger>
                        <SelectContent>
                          {products.map((prod) => (
                            <SelectItem key={prod.id} value={prod.id}>
                              {prod.sku} - {prod.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Cantidad</Label>
                      <Input
                        type="number"
                        min="0.01"
                        step="0.01"
                        value={line.quantity}
                        onChange={(e) => updateLine(index, 'quantity', e.target.value)}
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Almacén {formData.movement_type === 'TRANSFER' ? 'Origen' : ''}</Label>
                      <Select
                        value={line.warehouse_id}
                        onValueChange={(value) => updateLine(index, 'warehouse_id', value)}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Seleccionar" />
                        </SelectTrigger>
                        <SelectContent>
                          {warehouses.map((wh) => (
                            <SelectItem key={wh.id} value={wh.id}>
                              {wh.code} - {wh.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    {formData.movement_type === 'TRANSFER' ? (
                      <div className="space-y-2">
                        <Label>Almacén Destino</Label>
                        <Select
                          value={line.warehouse_destination_id}
                          onValueChange={(value) => updateLine(index, 'warehouse_destination_id', value)}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Seleccionar" />
                          </SelectTrigger>
                          <SelectContent>
                            {warehouses.filter(wh => wh.id !== line.warehouse_id).map((wh) => (
                              <SelectItem key={wh.id} value={wh.id}>
                                {wh.code} - {wh.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    ) : formData.movement_type === 'IN' ? (
                      <div className="space-y-2">
                        <Label>Costo Unitario</Label>
                        <Input
                          type="number"
                          min="0"
                          step="0.01"
                          value={line.unit_cost}
                          onChange={(e) => updateLine(index, 'unit_cost', e.target.value)}
                        />
                      </div>
                    ) : null}
                  </div>
                </div>
              ))}
            </div>

            <DialogFooter>
              <Button type="button" variant="secondary" onClick={() => setDialogOpen(false)}>
                Cancelar
              </Button>
              <Button 
                type="submit" 
                className="bg-blue-900 hover:bg-blue-800"
                disabled={saving}
                data-testid="movement-submit-btn"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                Registrar Movimiento
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MovementsPage;
